import { resolveAssessmentInterpretationText } from '../utils/interpretationOverride';
import {
  formatStepStopReasonLabel,
  type StepTestStopReason,
  type StepTestTimerBlock,
  type StepTestVitalsBlock,
} from '../step-tests/stepTestsDomain';

export interface ShuttleLevelDefinition {
  level: number;
  shuttles: number;
  speedMps: number;
  speedKmh: number;
  shuttleTimeSeconds: number;
  cumulativeShuttles: number;
  cumulativeDistanceMeters: number;
}

export interface ShuttlePacingEvent {
  atSeconds: number;
  kind: 'single' | 'triple';
  level?: number;
  shuttle?: number;
}

export interface ISWTAttemptPayload {
  attemptNumber: number;
  baseline: StepTestVitalsBlock;
  final: StepTestVitalsBlock;
  completedLevel: number;
  completedShuttlesOnLevel: number;
  completedShuttlesTotal: number;
  distanceMeters: number;
  peakLevelReached: number;
  peakSpeedMps: number | null;
  peakSpeedKmh: number | null;
  observedPeakHr: number | null;
  observedMinSpo2: number | null;
  stopReason: StepTestStopReason;
  timer: StepTestTimerBlock;
  notes?: string;
}

export interface ISWTProtocol {
  courseLengthMeters: number;
  totalLevels: number;
  audioPaced: boolean;
  duplicateAtBaseline: boolean;
  restIntervalMinutes: number;
  pacingOrigin: 'browser_synth';
  levels: ShuttleLevelDefinition[];
}

export interface ISWTResultPayload {
  protocol: ISWTProtocol;
  attempts: ISWTAttemptPayload[];
  notes?: string;
  patientContext?: Record<string, unknown>;
  professionalContext?: Record<string, unknown>;
  finishedAt: string;
  report?: {
    interpretation?: string;
    referenceSource?: string;
    audioSupportNote?: string;
  };
}

export interface ISWTSummary {
  attemptCount: number;
  bestAttemptNumber: number | null;
  bestDistanceMeters: number;
  bestLevelReached: number;
  bestLevelLabel: string;
  bestSpeedKmh: number | null;
  predictedDistanceMeters: number | null;
  percentPredicted: number | null;
  secondAttemptDeltaMeters: number | null;
  learningEffectDetected: boolean;
  minSpo2: number | null;
  peakHr: number | null;
  severity: string;
}

export interface ESWTProtocol {
  courseLengthMeters: number;
  audioPaced: boolean;
  pacingOrigin: 'browser_synth';
  speedPercentOfIswt: number;
  sourceIswtLevel: number | null;
  sourceIswtSpeedKmh: number | null;
  targetSpeedMps: number;
  targetSpeedKmh: number;
  shuttleIntervalSeconds: number;
  recommendedWindowMinutes: [number, number];
  maxDurationSeconds: number;
}

export interface ESWTResultPayload {
  protocol: ESWTProtocol;
  baseline: StepTestVitalsBlock;
  final: StepTestVitalsBlock;
  observedPeakHr: number | null;
  observedMinSpo2: number | null;
  totalTimeSeconds: number;
  totalShuttles: number;
  distanceMeters: number;
  stopReason: StepTestStopReason;
  timer: StepTestTimerBlock;
  notes?: string;
  patientContext?: Record<string, unknown>;
  professionalContext?: Record<string, unknown>;
  finishedAt: string;
  report?: {
    interpretation?: string;
    referenceSource?: string;
    audioSupportNote?: string;
  };
}

export interface ESWTSummary {
  durationSeconds: number;
  distanceMeters: number;
  speedKmh: number;
  speedPercentOfIswt: number;
  minSpo2: number | null;
  peakHr: number | null;
  withinRecommendedWindow: boolean | null;
  severity: string;
}

const ISWT_REFERENCE_SOURCE =
  'Holland et al. Eur Respir J. 2014;44:1428-1446. Singh et al. Thorax. 1992;47:1019-1024.';

const ESWT_REFERENCE_SOURCE =
  'Holland et al. Eur Respir J. 2014;44:1428-1446. Revill et al. Thorax. 1999;54(3):213-222. Hill et al. Respir Med. 2012;106(12):1665-1670.';

export const SHUTTLE_SYNTH_AUDIO_NOTE =
  'Pacer sintético gerado no navegador com beeps simples por shuttle e triplo beep por troca de estágio/minuto.';

export const ESWT_SPEED_PERCENT_OPTIONS = [70, 75, 80, 85] as const;

const buildISWTLevels = (): ShuttleLevelDefinition[] => {
  let cumulativeShuttles = 0;
  return Array.from({ length: 12 }, (_, index) => {
    const level = index + 1;
    const shuttles = level + 2;
    const speedMps = +(0.5 + index * 0.17).toFixed(2);
    const speedKmh = +(speedMps * 3.6).toFixed(2);
    const shuttleTimeSeconds = +(60 / shuttles).toFixed(2);
    cumulativeShuttles += shuttles;
    return {
      level,
      shuttles,
      speedMps,
      speedKmh,
      shuttleTimeSeconds,
      cumulativeShuttles,
      cumulativeDistanceMeters: cumulativeShuttles * 10,
    };
  });
};

export const ISWT_LEVELS: ShuttleLevelDefinition[] = buildISWTLevels();

const normalizeSex = (value: unknown): 'M' | 'F' | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toUpperCase();
  if (normalized === 'M' || normalized === 'MASCULINO' || normalized === 'MALE') return 'M';
  if (normalized === 'F' || normalized === 'FEMININO' || normalized === 'FEMALE') return 'F';
  return null;
};

const ISWT_PROTOCOL: ISWTProtocol = {
  courseLengthMeters: 10,
  totalLevels: 12,
  audioPaced: true,
  duplicateAtBaseline: true,
  restIntervalMinutes: 30,
  pacingOrigin: 'browser_synth',
  levels: ISWT_LEVELS,
};

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const readNonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const normalizeTimerBlock = (value: unknown, expectedSeconds: number): StepTestTimerBlock => {
  const source = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const elapsed = toFiniteNumber(source.elapsed_s) ?? 0;
  const startedAt = readNonEmptyString(source.startedAt);
  const endedAt = readNonEmptyString(source.endedAt);

  return {
    elapsed_s: Math.max(0, Math.round(elapsed)),
    completed: source.completed === true || elapsed >= expectedSeconds,
    startedAt,
    endedAt,
  };
};

const normalizeVitalsBlock = (value: unknown): StepTestVitalsBlock => {
  const source = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const bp = source.bp && typeof source.bp === 'object' ? (source.bp as Record<string, unknown>) : {};
  const borg = source.borg && typeof source.borg === 'object' ? (source.borg as Record<string, unknown>) : {};

  return {
    hr: readNonEmptyString(source.hr),
    spo2: readNonEmptyString(source.spo2),
    bp: {
      sys: readNonEmptyString(bp.sys),
      dia: readNonEmptyString(bp.dia),
    },
    borg: {
      dyspnea: toFiniteNumber(borg.dyspnea),
      legFatigue: toFiniteNumber(borg.legFatigue),
    },
  };
};

const getSeriesMin = (values: Array<number | null | undefined>): number | null => {
  const filtered = values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  return filtered.length ? Math.min(...filtered) : null;
};

const getSeriesMax = (values: Array<number | null | undefined>): number | null => {
  const filtered = values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  return filtered.length ? Math.max(...filtered) : null;
};

export const formatClock = (seconds: number): string => {
  const safe = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(safe / 60);
  const remainder = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
};

export const getISWTLevelDefinition = (level: number): ShuttleLevelDefinition | null =>
  ISWT_LEVELS.find((item) => item.level === level) ?? null;

const getCumulativeShuttlesBeforeLevel = (level: number): number => {
  if (level <= 1) return 0;
  return ISWT_LEVELS.slice(0, Math.max(0, level - 1)).reduce((sum, item) => sum + item.shuttles, 0);
};

export const calculateISWTCompletedShuttles = (completedLevel: number, completedShuttlesOnLevel: number): number => {
  const level = Math.max(0, Math.min(ISWT_PROTOCOL.totalLevels, Math.round(completedLevel)));
  if (level === 0) return 0;
  const definition = getISWTLevelDefinition(level);
  if (!definition) return 0;
  const clampedOnLevel = Math.max(0, Math.min(definition.shuttles, Math.round(completedShuttlesOnLevel)));
  return getCumulativeShuttlesBeforeLevel(level) + clampedOnLevel;
};

export const calculateISWTDistanceMeters = (completedLevel: number, completedShuttlesOnLevel: number): number =>
  calculateISWTCompletedShuttles(completedLevel, completedShuttlesOnLevel) * 10;

const resolvePeakLevelReached = (completedLevel: number, completedShuttlesOnLevel: number): number => {
  const level = Math.max(0, Math.min(ISWT_PROTOCOL.totalLevels, Math.round(completedLevel)));
  const definition = getISWTLevelDefinition(level);
  if (!definition) return 0;
  const clampedOnLevel = Math.max(0, Math.min(definition.shuttles, Math.round(completedShuttlesOnLevel)));
  if (clampedOnLevel > 0) return level;
  return Math.max(0, level - 1);
};

export const formatISWTLevelLabel = (completedLevel: number, completedShuttlesOnLevel: number): string => {
  const level = Math.max(0, Math.min(ISWT_PROTOCOL.totalLevels, Math.round(completedLevel)));
  const definition = getISWTLevelDefinition(level);
  if (!definition) return 'Sem nível registrado';
  const clampedOnLevel = Math.max(0, Math.min(definition.shuttles, Math.round(completedShuttlesOnLevel)));
  return `Nível ${level} + ${clampedOnLevel}/${definition.shuttles} shuttle(s)`;
};

export const getISWTPredictedDistance = (patientContext?: Record<string, unknown> | null): number | null => {
  const age = toFiniteNumber(patientContext?.ageYears);
  const sex = normalizeSex(patientContext?.sex);
  const weightKg = toFiniteNumber(patientContext?.weightKg);
  const heightCm = toFiniteNumber(patientContext?.heightCm);

  if (age == null || sex == null || weightKg == null || heightCm == null || heightCm <= 0) return null;
  const bmi = weightKg / Math.pow(heightCm / 100, 2);
  const sexCode = sex === 'M' ? 1 : 0;
  return +(1449.701 - 11.735 * age + 241.897 * sexCode - 5.686 * bmi).toFixed(1);
};

const normalizeISWTAttempt = (value: unknown, index: number): ISWTAttemptPayload => {
  const source = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const completedLevel = Math.max(0, Math.min(ISWT_PROTOCOL.totalLevels, Math.round(toFiniteNumber(source.completedLevel) ?? 0)));
  const levelDefinition = getISWTLevelDefinition(completedLevel);
  const completedShuttlesOnLevel = levelDefinition
    ? Math.max(0, Math.min(levelDefinition.shuttles, Math.round(toFiniteNumber(source.completedShuttlesOnLevel) ?? 0)))
    : 0;
  const completedShuttlesTotal = calculateISWTCompletedShuttles(completedLevel, completedShuttlesOnLevel);
  const distanceMeters = Math.max(
    0,
    Math.round(toFiniteNumber(source.distanceMeters) ?? completedShuttlesTotal * ISWT_PROTOCOL.courseLengthMeters),
  );
  const peakLevelReached = resolvePeakLevelReached(completedLevel, completedShuttlesOnLevel);
  const peakDefinition = getISWTLevelDefinition(peakLevelReached);

  return {
    attemptNumber: Math.max(1, Math.round(toFiniteNumber(source.attemptNumber) ?? index + 1)),
    baseline: normalizeVitalsBlock(source.baseline),
    final: normalizeVitalsBlock(source.final),
    completedLevel,
    completedShuttlesOnLevel,
    completedShuttlesTotal,
    distanceMeters,
    peakLevelReached,
    peakSpeedMps: peakDefinition?.speedMps ?? null,
    peakSpeedKmh: peakDefinition?.speedKmh ?? null,
    observedPeakHr: toFiniteNumber(source.observedPeakHr),
    observedMinSpo2: toFiniteNumber(source.observedMinSpo2),
    stopReason:
      (readNonEmptyString(source.stopReason) as StepTestStopReason | null) ??
      (distanceMeters >= ISWT_LEVELS[ISWT_LEVELS.length - 1].cumulativeDistanceMeters ? 'completed' : 'pace_loss'),
    timer: normalizeTimerBlock(source.timer, 720),
    notes: readNonEmptyString(source.notes) ?? undefined,
  };
};

export const normalizeISWTPayload = (value: unknown): ISWTResultPayload | null => {
  if (!value || typeof value !== 'object') return null;
  const source = value as Record<string, unknown>;
  const rawAttempts = Array.isArray(source.attempts) ? source.attempts : [];
  const attempts = rawAttempts.map((entry, index) => normalizeISWTAttempt(entry, index)).filter((entry) => entry.distanceMeters > 0 || entry.timer.elapsed_s > 0);
  if (!attempts.length) return null;

  return {
    protocol: {
      ...ISWT_PROTOCOL,
      pacingOrigin: 'browser_synth',
    },
    attempts,
    notes: readNonEmptyString(source.notes) ?? undefined,
    patientContext: source.patientContext && typeof source.patientContext === 'object' ? (source.patientContext as Record<string, unknown>) : undefined,
    professionalContext:
      source.professionalContext && typeof source.professionalContext === 'object'
        ? (source.professionalContext as Record<string, unknown>)
        : undefined,
    finishedAt: readNonEmptyString(source.finishedAt) ?? new Date().toISOString(),
    report: source.report && typeof source.report === 'object' ? (source.report as ISWTResultPayload['report']) : undefined,
  };
};

export const buildISWTPayload = (params: {
  attempts: Array<Partial<ISWTAttemptPayload>>;
  notes?: string;
  patientContext?: Record<string, unknown>;
  professionalContext?: Record<string, unknown>;
  finishedAt?: string;
}): ISWTResultPayload => {
  const attempts = params.attempts
    .map((entry, index) => normalizeISWTAttempt(entry, index))
    .filter((entry) => entry.distanceMeters > 0 || entry.timer.elapsed_s > 0);

  return {
    protocol: { ...ISWT_PROTOCOL },
    attempts,
    notes: params.notes?.trim() || undefined,
    patientContext: params.patientContext,
    professionalContext: params.professionalContext,
    finishedAt: params.finishedAt ?? new Date().toISOString(),
    report: {
      referenceSource: ISWT_REFERENCE_SOURCE,
      audioSupportNote: SHUTTLE_SYNTH_AUDIO_NOTE,
    },
  };
};

export const summarizeISWTResult = (value: unknown): ISWTSummary => {
  const payload = normalizeISWTPayload(value);
  if (!payload) {
    return {
      attemptCount: 0,
      bestAttemptNumber: null,
      bestDistanceMeters: 0,
      bestLevelReached: 0,
      bestLevelLabel: 'Sem dados',
      bestSpeedKmh: null,
      predictedDistanceMeters: null,
      percentPredicted: null,
      secondAttemptDeltaMeters: null,
      learningEffectDetected: false,
      minSpo2: null,
      peakHr: null,
      severity: 'Sem dados',
    };
  }

  const bestAttempt = [...payload.attempts].sort((left, right) => right.distanceMeters - left.distanceMeters)[0];
  const attemptOne = payload.attempts.find((item) => item.attemptNumber === 1) ?? payload.attempts[0];
  const attemptTwo = payload.attempts.find((item) => item.attemptNumber === 2);
  const secondAttemptDeltaMeters =
    attemptOne && attemptTwo ? Math.round(attemptTwo.distanceMeters - attemptOne.distanceMeters) : null;
  const predictedDistanceMeters = getISWTPredictedDistance(payload.patientContext);
  const percentPredicted =
    predictedDistanceMeters != null && predictedDistanceMeters > 0
      ? +(bestAttempt.distanceMeters / predictedDistanceMeters * 100).toFixed(1)
      : null;
  const minSpo2 = getSeriesMin(
    payload.attempts.flatMap((attempt) => [
      attempt.observedMinSpo2,
      toFiniteNumber(attempt.baseline.spo2),
      toFiniteNumber(attempt.final.spo2),
    ]),
  );
  const peakHr = getSeriesMax(
    payload.attempts.flatMap((attempt) => [
      attempt.observedPeakHr,
      toFiniteNumber(attempt.baseline.hr),
      toFiniteNumber(attempt.final.hr),
    ]),
  );

  let severity = payload.attempts.length >= 2 ? 'Protocolo completo' : 'Protocolo parcial';
  if (bestAttempt.stopReason === 'safety_stop' || bestAttempt.stopReason === 'supervisor_interruption') severity = 'Atenção de segurança';
  if (minSpo2 != null && minSpo2 < 80) severity = 'Atenção de segurança';
  if (bestAttempt.distanceMeters >= ISWT_LEVELS[ISWT_LEVELS.length - 1].cumulativeDistanceMeters) severity = 'Completou todos os níveis';

  return {
    attemptCount: payload.attempts.length,
    bestAttemptNumber: bestAttempt.attemptNumber,
    bestDistanceMeters: bestAttempt.distanceMeters,
    bestLevelReached: bestAttempt.peakLevelReached,
    bestLevelLabel: formatISWTLevelLabel(bestAttempt.completedLevel, bestAttempt.completedShuttlesOnLevel),
    bestSpeedKmh: bestAttempt.peakSpeedKmh,
    predictedDistanceMeters,
    percentPredicted,
    secondAttemptDeltaMeters,
    learningEffectDetected: secondAttemptDeltaMeters != null && secondAttemptDeltaMeters >= 20,
    minSpo2,
    peakHr,
    severity,
  };
};

export const resolveISWTInterpretation = (value: unknown): string => {
  const payload = normalizeISWTPayload(value);
  const summary = summarizeISWTResult(value);
  if (!payload) return 'Sem dados suficientes para interpretar o ISWT.';

  const bestAttempt = payload.attempts.find((item) => item.attemptNumber === summary.bestAttemptNumber) ?? payload.attempts[0];
  const parts: string[] = [
    `Foram registradas ${summary.attemptCount} tentativa(s) do ISWT em pista de 10 metros com pacing auditivo.`,
    `A melhor tentativa foi a ${summary.bestAttemptNumber ?? 1}, com ${summary.bestDistanceMeters} m e ${summary.bestLevelLabel.toLowerCase()}.`,
  ];

  if (summary.bestSpeedKmh != null) {
    parts.push(`A maior velocidade sustentada correspondeu a ${summary.bestSpeedKmh.toFixed(2)} km/h.`);
  }
  if (summary.predictedDistanceMeters != null && summary.percentPredicted != null) {
    parts.push(
      `Pela equação de Probst et al., a distância prevista é ${summary.predictedDistanceMeters.toFixed(1)} m, correspondendo a ${summary.percentPredicted.toFixed(1)}% do previsto.`,
    );
  }
  if (summary.secondAttemptDeltaMeters != null) {
    parts.push(
      summary.secondAttemptDeltaMeters > 0
        ? `A segunda tentativa superou a primeira em ${summary.secondAttemptDeltaMeters} m, compatível com efeito de aprendizagem.`
        : `A diferença entre a segunda e a primeira tentativa foi de ${summary.secondAttemptDeltaMeters} m.`,
    );
  }
  if (summary.minSpo2 != null) {
    parts.push(`A menor SpO2 observada foi ${summary.minSpo2.toFixed(0)}%.`);
  }
  if (summary.peakHr != null) {
    parts.push(`A FC pico observada foi ${summary.peakHr.toFixed(0)} bpm.`);
  }
  parts.push(`Encerramento da melhor tentativa: ${formatStepStopReasonLabel(bestAttempt.stopReason).toLowerCase()}.`);
  parts.push(`Situação observada: ${summary.severity.toLowerCase()}.`);
  return resolveAssessmentInterpretationText(payload, parts.join(' '));
};

export const deriveESWTTargetSpeed = (sourceIswtSpeedKmh: number, speedPercentOfIswt: number) => {
  const safeSource = Math.max(0, sourceIswtSpeedKmh);
  const safePercent = Math.max(0, speedPercentOfIswt);
  const targetSpeedKmh = +(safeSource * safePercent / 100).toFixed(2);
  const targetSpeedMps = +(targetSpeedKmh / 3.6).toFixed(2);
  const shuttleIntervalSeconds = targetSpeedKmh > 0 ? +(36 / targetSpeedKmh).toFixed(2) : 0;
  return {
    targetSpeedKmh,
    targetSpeedMps,
    shuttleIntervalSeconds,
  };
};

export const buildDefaultESWTProtocol = (params: {
  sourceIswtLevel?: number | null;
  sourceIswtSpeedKmh: number;
  speedPercentOfIswt?: number;
  maxDurationSeconds?: number;
}): ESWTProtocol => {
  const speedPercentOfIswt = params.speedPercentOfIswt ?? 85;
  const { targetSpeedKmh, targetSpeedMps, shuttleIntervalSeconds } = deriveESWTTargetSpeed(
    params.sourceIswtSpeedKmh,
    speedPercentOfIswt,
  );

  return {
    courseLengthMeters: 10,
    audioPaced: true,
    pacingOrigin: 'browser_synth',
    speedPercentOfIswt,
    sourceIswtLevel: params.sourceIswtLevel ?? null,
    sourceIswtSpeedKmh: params.sourceIswtSpeedKmh,
    targetSpeedMps,
    targetSpeedKmh,
    shuttleIntervalSeconds,
    recommendedWindowMinutes: [3, 8],
    maxDurationSeconds: params.maxDurationSeconds ?? 20 * 60,
  };
};

export const normalizeESWTPayload = (value: unknown): ESWTResultPayload | null => {
  if (!value || typeof value !== 'object') return null;
  const source = value as Record<string, unknown>;
  const protocolSource = source.protocol && typeof source.protocol === 'object' ? (source.protocol as Record<string, unknown>) : {};
  const sourceIswtSpeedKmh = Math.max(0, toFiniteNumber(protocolSource.sourceIswtSpeedKmh) ?? 0);
  const speedPercentOfIswt = Math.max(0, toFiniteNumber(protocolSource.speedPercentOfIswt) ?? 85);
  const baseProtocol = buildDefaultESWTProtocol({
    sourceIswtLevel: toFiniteNumber(protocolSource.sourceIswtLevel),
    sourceIswtSpeedKmh,
    speedPercentOfIswt,
    maxDurationSeconds: toFiniteNumber(protocolSource.maxDurationSeconds) ?? 20 * 60,
  });
  const timer = normalizeTimerBlock(source.timer, baseProtocol.maxDurationSeconds);
  const totalTimeSeconds = Math.max(0, Math.round(toFiniteNumber(source.totalTimeSeconds) ?? timer.elapsed_s));
  const totalShuttles = Math.max(
    0,
    Math.round(toFiniteNumber(source.totalShuttles) ?? (baseProtocol.shuttleIntervalSeconds > 0 ? Math.floor(totalTimeSeconds / baseProtocol.shuttleIntervalSeconds) : 0)),
  );
  const distanceMeters = Math.max(
    0,
    Math.round(toFiniteNumber(source.distanceMeters) ?? baseProtocol.targetSpeedMps * totalTimeSeconds),
  );

  if (totalTimeSeconds <= 0 && distanceMeters <= 0) return null;

  return {
    protocol: baseProtocol,
    baseline: normalizeVitalsBlock(source.baseline),
    final: normalizeVitalsBlock(source.final),
    observedPeakHr: toFiniteNumber(source.observedPeakHr),
    observedMinSpo2: toFiniteNumber(source.observedMinSpo2),
    totalTimeSeconds,
    totalShuttles,
    distanceMeters,
    stopReason:
      (readNonEmptyString(source.stopReason) as StepTestStopReason | null) ??
      (timer.completed ? 'completed' : 'patient_request'),
    timer,
    notes: readNonEmptyString(source.notes) ?? undefined,
    patientContext: source.patientContext && typeof source.patientContext === 'object' ? (source.patientContext as Record<string, unknown>) : undefined,
    professionalContext:
      source.professionalContext && typeof source.professionalContext === 'object'
        ? (source.professionalContext as Record<string, unknown>)
        : undefined,
    finishedAt: readNonEmptyString(source.finishedAt) ?? new Date().toISOString(),
    report: source.report && typeof source.report === 'object' ? (source.report as ESWTResultPayload['report']) : undefined,
  };
};

export const buildESWTPayload = (params: {
  protocol: ESWTProtocol;
  baseline: StepTestVitalsBlock;
  final: StepTestVitalsBlock;
  observedPeakHr?: number | null;
  observedMinSpo2?: number | null;
  totalTimeSeconds: number;
  stopReason: StepTestStopReason;
  timer: StepTestTimerBlock;
  notes?: string;
  patientContext?: Record<string, unknown>;
  professionalContext?: Record<string, unknown>;
  finishedAt?: string;
}): ESWTResultPayload => {
  const totalTimeSeconds = Math.max(0, Math.round(params.totalTimeSeconds));
  const totalShuttles =
    params.protocol.shuttleIntervalSeconds > 0 ? Math.floor(totalTimeSeconds / params.protocol.shuttleIntervalSeconds) : 0;
  const distanceMeters = Math.round(params.protocol.targetSpeedMps * totalTimeSeconds);

  return {
    protocol: params.protocol,
    baseline: params.baseline,
    final: params.final,
    observedPeakHr: params.observedPeakHr ?? null,
    observedMinSpo2: params.observedMinSpo2 ?? null,
    totalTimeSeconds,
    totalShuttles,
    distanceMeters,
    stopReason: params.stopReason,
    timer: params.timer,
    notes: params.notes?.trim() || undefined,
    patientContext: params.patientContext,
    professionalContext: params.professionalContext,
    finishedAt: params.finishedAt ?? new Date().toISOString(),
    report: {
      referenceSource: ESWT_REFERENCE_SOURCE,
      audioSupportNote: SHUTTLE_SYNTH_AUDIO_NOTE,
    },
  };
};

export const summarizeESWTResult = (value: unknown): ESWTSummary => {
  const payload = normalizeESWTPayload(value);
  if (!payload) {
    return {
      durationSeconds: 0,
      distanceMeters: 0,
      speedKmh: 0,
      speedPercentOfIswt: 0,
      minSpo2: null,
      peakHr: null,
      withinRecommendedWindow: null,
      severity: 'Sem dados',
    };
  }

  const minSpo2 = getSeriesMin([
    payload.observedMinSpo2,
    toFiniteNumber(payload.baseline.spo2),
    toFiniteNumber(payload.final.spo2),
  ]);
  const peakHr = getSeriesMax([
    payload.observedPeakHr,
    toFiniteNumber(payload.baseline.hr),
    toFiniteNumber(payload.final.hr),
  ]);
  const [minWindow, maxWindow] = payload.protocol.recommendedWindowMinutes;
  const withinRecommendedWindow =
    payload.totalTimeSeconds > 0 ? payload.totalTimeSeconds >= minWindow * 60 && payload.totalTimeSeconds <= maxWindow * 60 : null;

  let severity = 'Endurance prolongada';
  if (payload.stopReason === 'safety_stop' || payload.stopReason === 'supervisor_interruption') severity = 'Atenção de segurança';
  if (minSpo2 != null && minSpo2 < 80) severity = 'Atenção de segurança';
  else if (payload.totalTimeSeconds < minWindow * 60) severity = 'Tempo abaixo da faixa alvo';
  else if (withinRecommendedWindow) severity = 'Faixa alvo 3-8 min';

  return {
    durationSeconds: payload.totalTimeSeconds,
    distanceMeters: payload.distanceMeters,
    speedKmh: payload.protocol.targetSpeedKmh,
    speedPercentOfIswt: payload.protocol.speedPercentOfIswt,
    minSpo2,
    peakHr,
    withinRecommendedWindow,
    severity,
  };
};

export const resolveESWTInterpretation = (value: unknown): string => {
  const payload = normalizeESWTPayload(value);
  const summary = summarizeESWTResult(value);
  if (!payload) return 'Sem dados suficientes para interpretar o ESWT.';

  const parts: string[] = [
    `O ESWT foi realizado em pista de 10 metros com pacing auditivo a ${payload.protocol.targetSpeedKmh.toFixed(2)} km/h.`,
    `A velocidade foi definida em ${payload.protocol.speedPercentOfIswt}% da velocidade pico do ISWT.`,
    `O tempo tolerado foi ${formatClock(summary.durationSeconds)} e a distância estimada foi ${summary.distanceMeters} m.`,
  ];

  if (payload.protocol.sourceIswtLevel != null) {
    parts.push(`O ajuste foi baseado no nível ${payload.protocol.sourceIswtLevel} do ISWT.`);
  }
  if (summary.withinRecommendedWindow != null) {
    parts.push(
      summary.withinRecommendedWindow
        ? 'O tempo ficou dentro da faixa operacional de 3 a 8 minutos.'
        : 'O tempo ficou fora da faixa operacional de 3 a 8 minutos para o primeiro ajuste do teste.',
    );
  }
  if (summary.minSpo2 != null) {
    parts.push(`A menor SpO2 observada foi ${summary.minSpo2.toFixed(0)}%.`);
  }
  if (summary.peakHr != null) {
    parts.push(`A FC pico observada foi ${summary.peakHr.toFixed(0)} bpm.`);
  }
  parts.push(`Encerramento: ${formatStepStopReasonLabel(payload.stopReason).toLowerCase()}.`);
  parts.push(`Situação observada: ${summary.severity.toLowerCase()}.`);
  return resolveAssessmentInterpretationText(payload, parts.join(' '));
};

export const buildISWTPacingEvents = (): ShuttlePacingEvent[] =>
  ISWT_LEVELS.flatMap((level) => {
    const stageStart = (level.level - 1) * 60;
    const singles = Array.from({ length: level.shuttles }, (_, index) => ({
      atSeconds: +(stageStart + (index + 1) * level.shuttleTimeSeconds).toFixed(2),
      kind: 'single' as const,
      level: level.level,
      shuttle: index + 1,
    }));

    return [{ atSeconds: stageStart, kind: 'triple' as const, level: level.level }, ...singles];
  });

export const buildESWTPacingEvents = (protocol: ESWTProtocol): ShuttlePacingEvent[] => {
  if (protocol.shuttleIntervalSeconds <= 0) return [];
  const events: ShuttlePacingEvent[] = [];
  for (let minuteStart = 0; minuteStart <= protocol.maxDurationSeconds; minuteStart += 60) {
    events.push({ atSeconds: minuteStart, kind: 'triple' });
  }
  for (let elapsed = protocol.shuttleIntervalSeconds; elapsed <= protocol.maxDurationSeconds; elapsed += protocol.shuttleIntervalSeconds) {
    events.push({ atSeconds: +elapsed.toFixed(2), kind: 'single' });
  }
  return events.sort((left, right) => left.atSeconds - right.atSeconds);
};

export const ISWT_PROTOCOL_METADATA = {
  source: ISWT_REFERENCE_SOURCE,
  description:
    'Pista reta de 10 m, 12 níveis de 1 minuto, início em 0,50 m/s, incremento de 0,17 m/s por estágio, com duas tentativas na avaliação basal. Distância prevista calculável pela equação de Probst et al. 2011 usando idade, sexo e IMC.',
};

export const ESWT_PROTOCOL_METADATA = {
  source: ESWT_REFERENCE_SOURCE,
  description:
    'Pista reta de 10 m, pacing auditivo constante e velocidade derivada do ISWT, preferencialmente em 85% da velocidade pico do teste incremental.',
};
